/* eslint-disable no-param-reassign */
import axios from 'axios';
import catchify from 'catchify';

import { getCSRFCookie } from '~/util/global';

const buildApiRequest = (
  axiosInstance = axios,
  url,
  method = 'get',
  data = {},
  headers = { 'Content-Type': 'multipart/form-data' },
) => (
  catchify(axiosInstance({
    data,
    url,
    method,
    withCredentials: true,
    headers: {
      'X-CSRFToken': getCSRFCookie(),
      ...headers,
    },
  }))
);

const initialState = () => ({
  redemptionData: null,
  isProcessing: false,
  isRefundRequestCompleted: false,
  refundFailed: false,
  extendedActivation: false,
  successfullyRedeemed: false,
});

export const mutations = {
  setRedemptionData(state, data) {
    state.redemptionData = data;
  },
  setIsProcessing(state, value) {
    state.isProcessing = value;
  },
  setIsRefundRequestCompleted(state, value) {
    state.isRefundRequestCompleted = value;
  },
  setRefundFailed(state, value) {
    state.refundFailed = value;
  },
  setExtendedActivation(state, data) {
    state.extendedActivation = data;
  },
  setSuccessfullyRedeemed(state, data) {
    state.successfullyRedeemed = data;
  },
};

export const getters = {
  redemptionData: (state) => state.redemptionData,
  isProcessing: (state) => state.isProcessing,
  isRefundRequestCompleted: (state) => state.isRefundRequestCompleted,
  refundFailed: (state) => state.refundFailed,
  extendedActivation: (state) => state.extendedActivation,
  successfullyRedeemed: (state) => state.successfullyRedeemed,
};

export const actions = {
  async extendActivationDate({ commit }, uuid) {
    commit('setExtendedActivation', false);

    const data = new FormData();
    data.append('uuid', uuid);

    const [err] = await buildApiRequest(
      this.$axios,
      '/api/cart/extend-activation/',
      'post',
      data,
    );

    if (!err) {
      commit('setExtendedActivation', true);
    }
  },

  async markProductRedeemed({ commit }, itemId) {
    commit('setIsProcessing', true);
    commit('setSuccessfullyRedeemed', false);

    const data = new FormData();
    data.append('id', itemId);

    const [err] = await buildApiRequest(
      this.$axios,
      '/cart/redeem/success/',
      'post',
      data,
    );

    commit('setIsProcessing', false);
    if (!err) {
      commit('setSuccessfullyRedeemed', true);
    }
  },

  async updateRedemptionData({ commit }, uuid) {
    const [error, response] = await buildApiRequest(
      this.$axios,
      `/api/cart/redemption-data/?uuid=${uuid}`,
      'get',
    );

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching redemption data -', error);
    } else {
      commit('setRedemptionData', response.data);
    }
  },

  async postNormalRefund({ commit }, { uuid, refundType }) {
    commit('setIsProcessing', true);

    const data = new FormData();
    data.append('id', uuid);
    data.append('type', refundType);

    const [err] = await buildApiRequest(
      this.$axios,
      '/cart/refund/',
      'post',
      data,
    );

    commit('setIsProcessing', false);
    commit('setIsRefundRequestCompleted', true);
    if (err) {
      commit('setRefundFailed', true);
    }
  },

  async postLicenseRefund({ commit }, {
    invoiceItemKey,
    dealPlanKey,
    refundType,
    apiUrl,
    httpMethod,
  }) {
    commit('setIsProcessing', true);

    const data = new FormData();
    data.append('invoice_item', invoiceItemKey);
    data.append('plan', dealPlanKey);
    data.append('refund_type', refundType);
    data.append('action', 'refund');

    const [err] = await buildApiRequest(
      this.$axios,
      apiUrl,
      httpMethod,
      data,
    );

    commit('setIsProcessing', false);
    commit('setIsRefundRequestCompleted', true);
    if (err) {
      commit('setRefundFailed', true);
    }
  },

  async activateProduct({ commit }, {
    invoiceItemKey,
    userEmail,
  }) {
    commit('setIsProcessing', true);

    const data = new FormData();
    data.append('invoice_item', invoiceItemKey);
    data.append('activation_email', userEmail);
    data.append('action', 'activate');

    const [error, response] = await buildApiRequest(
      this.$axios,
      '/license/activate/',
      'post',
      data,
    );

    commit('setIsProcessing', false);

    return [error, response?.data];
  },
};

export { initialState as state };
